import React, { useRef, useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config/api';
import { formatColorName } from '../constants/wigOptions';
import { getCurrentUser } from '../utils/auth';
import LegacyWorksheetSheet, { LEGACY_PRINT_STYLES, sortTasks } from './OrderWorksheetPrint';

// On-screen worksheet styles. Scoped under .ows-sheet so they can't collide with
// page styles. The printed copy is unchanged and lives in OrderWorksheetPrint.jsx.
const SHEET_CSS = `
  .ows-sheet {
    --ink: oklch(0.2 0.01 350);
    --ink-2: oklch(0.42 0.012 350);
    --ink-3: oklch(0.58 0.01 350);
    --rule: oklch(0.82 0.008 350);
    --rule-strong: oklch(0.25 0.01 350);
    --paper: oklch(0.995 0.002 350);
    --zebra: oklch(0.965 0.006 350);
    --accent: oklch(0.58 0.19 350);
    --accent-tint: oklch(0.93 0.05 350);
    --due-label: var(--accent-tint);

    font-family: 'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif;
    font-size: 11px;
    line-height: 1.35;
    color: var(--ink);
    background: var(--paper);
    font-variant-numeric: tabular-nums;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .ows-sheet *, .ows-sheet *::before, .ows-sheet *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ows-label {
    font-size: 8.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-3);
  }
  .ows-mono { font-family: 'JetBrains Mono', 'Cascadia Mono', Consolas, monospace; }

  /* Masthead */
  .ows-masthead {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 16px;
    align-items: end;
    padding-bottom: 10px;
    border-bottom: 2.5px solid var(--rule-strong);
  }
  .ows-brand {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 4px;
  }
  .ows-customer {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }
  .ows-subline { margin-top: 3px; font-size: 12px; font-weight: 600; color: var(--ink-2); }
  .ows-subline span + span::before { content: '·'; margin: 0 6px; color: var(--ink-3); }

  .ows-ids { display: flex; gap: 8px; align-items: stretch; }
  .ows-id {
    border: 1.5px solid var(--rule-strong);
    border-radius: 4px;
    padding: 5px 10px 6px;
    min-width: 110px;
  }
  .ows-id-value { display: block; margin-top: 2px; font-size: 17px; font-weight: 700; letter-spacing: 0.02em; }
  .ows-id--due { background: var(--ink); border-color: var(--ink); color: var(--paper); }
  .ows-id--due .ows-label { color: var(--due-label); }

  /* Order facts strip */
  .ows-facts {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-bottom: 1px solid var(--rule);
    margin-bottom: 16px;
  }
  .ows-fact { padding: 7px 10px 7px 0; }
  .ows-fact + .ows-fact { padding-left: 10px; border-left: 1px solid var(--rule); }
  .ows-fact-value { display: block; margin-top: 1px; font-size: 12px; font-weight: 600; }

  /* Section headings */
  .ows-section-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .ows-section-title { font-size: 13px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
  .ows-legend { display: flex; gap: 12px; font-size: 9px; color: var(--ink-2); }
  .ows-legend span { display: inline-flex; align-items: center; gap: 4px; }

  /* Routing table */
  .ows-scroll { overflow-x: auto; }
  .ows-routing { width: 100%; border-collapse: collapse; }
  .ows-routing th {
    text-align: left;
    padding: 5px 6px;
    border-top: 1.5px solid var(--rule-strong);
    border-bottom: 1.5px solid var(--rule-strong);
    white-space: nowrap;
  }
  .ows-routing td {
    padding: 6px;
    border-bottom: 1px solid var(--rule);
    vertical-align: top;
    font-size: 11.5px;
  }
  .ows-routing tbody tr:nth-child(even) td { background: var(--zebra); }
  .ows-routing tbody tr { break-inside: avoid; }
  .ows-routing tr.is-done td { color: var(--ink-2); }

  .ows-col-step { width: 22px; color: var(--ink-3); font-weight: 600; text-align: right; }
  .ows-col-room { width: 58px; font-size: 14px !important; font-weight: 800; white-space: nowrap; }
  .ows-col-task { width: 19%; font-weight: 600; }
  .ows-col-notes { white-space: pre-line; }
  .ows-col-worker { width: 12%; }
  .ows-col-due { width: 74px; white-space: nowrap; text-align: right; }
  .ows-col-status { width: 46px; text-align: center; }
  .ows-routing th.ows-col-due { text-align: right; }
  .ows-routing th.ows-col-status { text-align: center; }
  .ows-empty-cell { color: var(--ink-3); }

  /* Status box: empty = not started (tick it by pen), dash = in progress, check = done */
  .ows-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    border: 1.5px solid var(--rule-strong);
    border-radius: 3px;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    vertical-align: middle;
  }
  .ows-box--done { background: var(--ink); color: var(--paper); border-color: var(--ink); }
  .ows-box--progress { color: var(--ink); }

  .ows-empty-row td {
    padding: 22px 6px;
    text-align: center;
    color: var(--ink-3);
    background: transparent !important;
  }

  /* Build specifications */
  .ows-specs { margin-top: 18px; break-inside: avoid; }
  .ows-spec-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-top: 1.5px solid var(--rule-strong);
  }
  .ows-spec {
    padding: 7px 10px 8px 0;
    border-bottom: 1px solid var(--rule);
  }
  .ows-spec:not(:nth-child(4n + 1)) { padding-left: 10px; border-left: 1px solid var(--rule); }
  .ows-spec-value { display: block; margin-top: 2px; font-size: 12.5px; font-weight: 700; }
  .ows-spec-value.is-missing { color: var(--ink-3); font-weight: 500; }
  .ows-spec--wide { grid-column: span 2; }

  /* Footer */
  .ows-footer {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 16px;
    padding-top: 6px;
    border-top: 1px solid var(--rule);
    font-size: 9px;
    color: var(--ink-3);
  }

  @media (max-width: 640px) {
    .ows-masthead { grid-template-columns: 1fr; }
    .ows-facts, .ows-spec-grid { grid-template-columns: repeat(2, 1fr); }
    .ows-fact:nth-child(odd) { padding-left: 0; border-left: 0; }
    .ows-spec:not(:nth-child(4n + 1)) { padding-left: 0; border-left: 0; }
    .ows-spec:nth-child(even) { padding-left: 10px; border-left: 1px solid var(--rule); }
    .ows-routing { min-width: 680px; }
  }
`;

// Modal chrome (screen only)
const MODAL_CSS = `
  .ows-overlay {
    --chrome-scrim: oklch(0.25 0.01 350 / 0.55);
    --chrome-stage: oklch(0.955 0.005 350);
    --chrome-bar: oklch(0.985 0.003 350);
    --chrome-line: oklch(0.88 0.006 350);
    --chrome-text: oklch(0.2 0.01 350);
    --chrome-muted: oklch(0.5 0.01 350);
    --chrome-shadow: oklch(0.2 0.02 350 / 0.35);
    --btn-bg: oklch(0.995 0.002 350);
    --btn-bg-hover: oklch(0.96 0.006 350);
    --btn-line: oklch(0.82 0.008 350);
    --btn-line-hover: oklch(0.7 0.01 350);
    --btn-text: oklch(0.25 0.01 350);
    --btn-primary: oklch(0.6 0.24 350);
    --btn-primary-hover: oklch(0.55 0.23 350);
    --btn-primary-text: oklch(0.99 0.004 350);
    --focus: oklch(0.58 0.19 350);
    --paper-bg: oklch(0.995 0.002 350);
    --paper-shadow: 0 1px 2px oklch(0.2 0.01 350 / 0.08), 0 8px 24px -8px oklch(0.2 0.01 350 / 0.18);

    position: fixed;
    inset: 0;
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: var(--chrome-scrim);
    color-scheme: light;
  }
  .ows-modal {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1000px;
    max-height: calc(100vh - 32px);
    overflow: hidden;
    border-radius: 10px;
    background: var(--chrome-stage);
    box-shadow: 0 24px 48px -16px var(--chrome-shadow);
    animation: ows-in 200ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes ows-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

  .ows-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--chrome-line);
    background: var(--chrome-bar);
  }
  .ows-toolbar-title { font-size: 15px; font-weight: 700; color: var(--chrome-text); margin: 0; }
  .ows-toolbar-title small { display: block; font-size: 12px; font-weight: 500; color: var(--chrome-muted); }
  .ows-actions { display: flex; gap: 8px; }
  .ows-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 6px;
    border: 1px solid var(--btn-line);
    background: var(--btn-bg);
    color: var(--btn-text);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 150ms ease-out, border-color 150ms ease-out;
  }
  .ows-btn:hover { background: var(--btn-bg-hover); border-color: var(--btn-line-hover); }
  .ows-btn:focus-visible { outline: 2px solid var(--focus); outline-offset: 2px; }
  .ows-btn--primary { background: var(--btn-primary); border-color: var(--btn-primary); color: var(--btn-primary-text); }
  .ows-btn--primary:hover { background: var(--btn-primary-hover); border-color: var(--btn-primary-hover); }

  .ows-stage { flex: 1; overflow: auto; padding: 24px; }
  .ows-paper {
    max-width: 8.5in;
    margin: 0 auto;
    padding: 0.45in;
    background: var(--paper-bg);
    box-shadow: var(--paper-shadow);
  }

  /* Dark theme: the app is dark unless <html> has .light-theme (see ThemeToggle).
     Neutrals match the app's #121212 / #262626 / #2c2c2c surfaces. */
  :root:not(.light-theme) .ows-overlay {
    --chrome-scrim: oklch(0.05 0 0 / 0.72);
    --chrome-stage: oklch(0.185 0.003 350);
    --chrome-bar: oklch(0.225 0.004 350);
    --chrome-line: oklch(0.31 0.005 350);
    --chrome-text: oklch(0.94 0.004 350);
    --chrome-muted: oklch(0.72 0.006 350);
    --chrome-shadow: oklch(0.02 0 0 / 0.7);
    --btn-bg: oklch(0.27 0.004 350);
    --btn-bg-hover: oklch(0.32 0.005 350);
    --btn-line: oklch(0.36 0.006 350);
    --btn-line-hover: oklch(0.46 0.008 350);
    --btn-text: oklch(0.92 0.004 350);
    --focus: oklch(0.74 0.17 350);
    --paper-bg: oklch(0.225 0.004 350);
    --paper-shadow: 0 0 0 1px oklch(0.31 0.005 350), 0 12px 32px -12px oklch(0.02 0 0 / 0.7);
    color-scheme: dark;
  }
  :root:not(.light-theme) .ows-paper .ows-sheet {
    --ink: oklch(0.94 0.004 350);
    --ink-2: oklch(0.8 0.006 350);
    --ink-3: oklch(0.66 0.006 350);
    --rule: oklch(0.32 0.005 350);
    --rule-strong: oklch(0.72 0.006 350);
    --paper: oklch(0.225 0.004 350);
    --zebra: oklch(0.255 0.004 350);
    --accent: oklch(0.74 0.17 350);
    --due-label: oklch(0.5 0.2 350);
  }

  /* Shield the modal from the app's global element styles (App.css styles bare
     table/th/td/tr/h*/button with theme colors, padding, shadows and hover lift). */
  .ows-overlay h5, .ows-overlay h2 { margin: 0; }
  .ows-overlay .ows-section-title { color: var(--ink); }
  .ows-overlay table.ows-routing {
    margin: 0;
    border-radius: 0;
    overflow: visible;
    background: transparent;
    transition: none;
  }
  .ows-overlay .ows-routing th { background: transparent; color: var(--ink-3); font-weight: 600; transition: none; }
  .ows-overlay .ows-routing td { color: var(--ink); background: transparent; transition: none; }
  .ows-overlay .ows-routing tbody tr:nth-child(even) td { background: var(--zebra); }
  .ows-overlay .ows-routing tr.is-done td { color: var(--ink-2); }
  .ows-overlay .ows-routing tr:last-child td { border-bottom: 1px solid var(--rule); }
  .ows-overlay .ows-routing tr:hover { background: transparent; }
  .ows-overlay .ows-routing td.ows-col-step,
  .ows-overlay .ows-empty-row td { color: var(--ink-3); }
  .ows-overlay .ows-btn,
  .ows-overlay .ows-btn:hover { box-shadow: none; transform: none; }

  @media (max-width: 640px) {
    .ows-overlay { padding: 0; }
    .ows-modal { max-height: 100vh; border-radius: 0; }
    .ows-stage { padding: 12px; }
    .ows-paper { padding: 16px; }
    .ows-btn-text { display: none; }
  }
`;

const STATUS = {
  done: { rank: 1, glyph: '✓', className: 'ows-box ows-box--done', label: 'Done' },
  progress: { rank: 2, glyph: '–', className: 'ows-box ows-box--progress', label: 'In progress' },
  todo: { rank: 3, glyph: '', className: 'ows-box', label: 'Not started' }
};

const getStatus = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('complete') || s.includes('done')) return STATUS.done;
  if (!s || s.includes('not started') || s === 'new') return STATUS.todo;
  return STATUS.progress;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// Notes are stored with literal "\n" sequences
const formatNotes = (notes) => {
  if (!notes) return '';
  const lines = notes.split(/\\n/);
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

// The new printed layout is not approved yet, so only reviewers get the
// "Print new design" button. Everyone else only ever gets the original printout.
// Anyone else can opt in for one browser tab by opening the app with ?draftPrint=1.
const DRAFT_PRINT_USERS = ['noga', 'didip', 'rachelg'];

const isDraftPrintEnabled = () => {
  if (DRAFT_PRINT_USERS.includes(String(getCurrentUser()).toLowerCase())) return true;
  try {
    if (/[?&]draftPrint=1/.test(window.location.href)) sessionStorage.setItem('owsDraftPrint', '1');
    return sessionStorage.getItem('owsDraftPrint') === '1';
  } catch {
    return false;
  }
};

const OrderWorksheet = ({
  orderDetails,
  customerInfo,
  wigDetails,
  isVisible = false,
  onClose,
  roomTasks = []
}) => {
  const worksheetRef = useRef();
  const draftSheetRef = useRef();
  const [enhancedRoomTasks, setEnhancedRoomTasks] = useState(roomTasks);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch room tasks with TaskStatus from the dedicated endpoint
  useEffect(() => {
    const fetchRoomTasksWithStatus = async () => {
      setDataLoaded(false);
      try {
        const response = await fetch(`${API_URL}/api/room-tasks/${orderDetails.orderNo}`);
        setEnhancedRoomTasks(response.ok ? await response.json() : roomTasks);
      } catch (error) {
        console.error('Error fetching room tasks with status:', error);
        setEnhancedRoomTasks(roomTasks);
      } finally {
        setDataLoaded(true);
      }
    };

    if (isVisible && orderDetails?.orderNo) {
      fetchRoomTasksWithStatus();
    }
  }, [isVisible, orderDetails?.orderNo, roomTasks]);

  // Print the original worksheet layout (hidden below), unchanged from before the redesign
  const handlePrint = useCallback(() => {
    const printContent = worksheetRef.current;
    
    if (!printContent) {
      console.error('Print content not found');
      return;
    }
    
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      
      if (!printWindow) {
        console.error('Failed to open print window - popup might be blocked');
        alert('Please allow popups for printing');
        return;
      }
      
      const printStyles = LEGACY_PRINT_STYLES;

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Work Order Worksheet - #${orderDetails?.orderNo || 'N/A'}</title>
          <meta charset="utf-8">
          ${printStyles}
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
        </html>
      `;
      
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setTimeout(() => {
        try {
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => {
              if (printWindow && !printWindow.closed) {
                printWindow.close();
              }
            }, 1000);
          }, 500);
        } catch (printError) {
          console.error('Error during printing:', printError);
          alert('Error occurred while preparing print. Please try again.');
        }
      }, 100);
      
    } catch (error) {
      console.error('Error in print function:', error);
      alert('Error occurred while preparing print. Please try again.');
    }
  }, [orderDetails?.orderNo]);

  // Draft: print the redesigned sheet (always in its light, paper colors)
  const handlePrintDraft = useCallback(() => {
    const sheet = draftSheetRef.current;
    if (!sheet) return;
    const printWindow = window.open('', '_blank', 'width=850,height=1000,scrollbars=yes,resizable=yes');
    if (!printWindow) {
      alert('Please allow popups for printing');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(`<!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Worksheet (new design draft) #${orderDetails?.orderNo || ''}</title>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap">
        <style>
          @page { margin: 0.4in; size: letter; }
          html, body { margin: 0; padding: 0; background: #fff; }
          ${SHEET_CSS}
        </style>
      </head>
      <body>${sheet.outerHTML}</body>
      </html>`);
    printWindow.document.close();
    const doPrint = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => {
        if (!printWindow.closed) printWindow.close();
      }, 1000);
    };
    // Wait for the fonts so the sheet doesn't print in the fallback face
    const fontsReady = printWindow.document.fonts?.ready || Promise.resolve();
    Promise.race([fontsReady, new Promise((r) => setTimeout(r, 1500))]).then(() => setTimeout(doPrint, 200));
  }, [orderDetails?.orderNo]);

  // Auto-open print dialog once the tasks are loaded
  useEffect(() => {
    if (isVisible && dataLoaded) {
      const t = setTimeout(handlePrint, 300);
      return () => clearTimeout(t);
    }
  }, [isVisible, dataLoaded, handlePrint]);

  useEffect(() => {
    if (!isVisible) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const pick = (...values) => values.find((v) => v !== undefined && v !== null && v !== '');

  const orderNo = orderDetails?.orderNo || '';
  const wigCode = pick(orderDetails?.wigBarCode, orderDetails?.orderNo) || '';
  const customerName = pick(customerInfo?.customerName, orderDetails?.orderCustomerName) || 'No customer name';
  const orderType = orderDetails?.orderType;
  const showSpecs = orderType !== 'Consultation' && orderType !== 'Accessory';

  const tasks = sortTasks(
    (enhancedRoomTasks || []).filter((task) => !(task.TaskStatus || '').toLowerCase().includes('reject'))
  );

  const facts = [
    { label: 'Order date', value: formatDate(orderDetails?.orderDate) },
    { label: 'Order type', value: orderType },
    { label: 'Phone', value: pick(customerInfo?.phone, orderDetails?.orderPhone) },
    { label: 'Mold ID', value: pick(orderDetails?.moldId, customerInfo?.moldId) }
  ];

  const weight = pick(wigDetails?.wigWeight, orderDetails?.wigWeight);
  const measurements = [
    pick(wigDetails?.hairlineToCrown, orderDetails?.hairlineToCrown),
    pick(wigDetails?.hairlineToNape, orderDetails?.hairlineToNape),
    pick(wigDetails?.earToEar, orderDetails?.earToEar)
  ].filter(Boolean).join(' / ');

  const specs = [
    { label: 'Base color', value: formatColorName(pick(wigDetails?.baseColor, orderDetails?.baseColor)) },
    { label: 'Wig type', value: pick(wigDetails?.wigType, orderDetails?.wigType) },
    { label: 'Hair length', value: pick(wigDetails?.hairLength, orderDetails?.wigLength) },
    { label: 'Hair type', value: pick(wigDetails?.hairType, orderDetails?.hairType) },
    { label: 'Net size', value: pick(wigDetails?.netSize, orderDetails?.netSize) },
    { label: 'Net type', value: pick(wigDetails?.netType, orderDetails?.netType) },
    { label: 'Lace type', value: pick(wigDetails?.laceType, orderDetails?.laceType) },
    { label: 'Top size', value: pick(wigDetails?.topSize, orderDetails?.topSize) },
    { label: 'Wig serial', value: pick(orderDetails?.wigSerialNo, orderDetails?.wigSerialCode), mono: true },
    { label: 'Weight', value: weight !== undefined ? `${weight} g` : '' },
    { label: 'Crown / Nape / Ear to ear', value: measurements, wide: true }
  ];

  const generatedAt = new Date().toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return (
    <div className="ows-overlay" onClick={onClose}>
      <style>{MODAL_CSS + SHEET_CSS}</style>
      <div hidden>
        <LegacyWorksheetSheet
          ref={worksheetRef}
          orderDetails={orderDetails}
          customerInfo={customerInfo}
          wigDetails={wigDetails}
          roomTasks={enhancedRoomTasks}
        />
      </div>
      <div
        className="ows-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ows-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ows-toolbar">
          <h5 id="ows-title" className="ows-toolbar-title">
            Production worksheet
            <small>Order #{orderNo} · {customerName}</small>
          </h5>
          <div className="ows-actions">
            <button type="button" className="ows-btn ows-btn--primary" onClick={handlePrint}>
              <i className="fas fa-print" aria-hidden="true"></i>
              <span className="ows-btn-text">Print</span>
            </button>
            {isDraftPrintEnabled() && (
              <button type="button" className="ows-btn" onClick={handlePrintDraft}>
                <i className="fas fa-flask" aria-hidden="true"></i>
                <span className="ows-btn-text">Print new design (draft)</span>
              </button>
            )}
            <button type="button" className="ows-btn" onClick={onClose} aria-label="Close worksheet">
              <i className="fas fa-times" aria-hidden="true"></i>
              <span className="ows-btn-text">Close</span>
            </button>
          </div>
        </div>

        <div className="ows-stage">
          <div className="ows-paper">
            <div ref={draftSheetRef} className="ows-sheet">
              <header className="ows-masthead">
                <div>
                  <div className="ows-brand">DiNi Wigs · Production worksheet</div>
                  <div className="ows-customer">{customerName}</div>
                  <div className="ows-subline">
                    <span>Order #{orderNo}</span>
                    {orderType && <span>{orderType}</span>}
                  </div>
                </div>
                <div className="ows-ids">
                  <div className="ows-id">
                    <span className="ows-label">Wig code</span>
                    <span className="ows-id-value ows-mono">{wigCode || 'Not set'}</span>
                  </div>
                  <div className="ows-id ows-id--due">
                    <span className="ows-label">Due</span>
                    <span className="ows-id-value">{formatDate(orderDetails?.dueDate) || 'Not set'}</span>
                  </div>
                </div>
              </header>

              <div className="ows-facts">
                {facts.map((f) => (
                  <div key={f.label} className="ows-fact">
                    <span className="ows-label">{f.label}</span>
                    <span className="ows-fact-value">{f.value || 'Not set'}</span>
                  </div>
                ))}
              </div>

              <section>
                <div className="ows-section-head">
                  <h2 className="ows-section-title">Room routing</h2>
                  <div className="ows-legend">
                    {[STATUS.done, STATUS.progress, STATUS.todo].map((s) => (
                      <span key={s.label}>
                        <span className={s.className}>{s.glyph}</span>
                        {s.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="ows-scroll">
                  <table className="ows-routing">
                    <thead>
                      <tr>
                        <th className="ows-label ows-col-step">#</th>
                        <th className="ows-label ows-col-room">Room</th>
                        <th className="ows-label ows-col-task">Task</th>
                        <th className="ows-label ows-col-notes">Task notes</th>
                        <th className="ows-label ows-col-notes">QC notes</th>
                        <th className="ows-label ows-col-worker">Worker</th>
                        <th className="ows-label ows-col-due">Due</th>
                        <th className="ows-label ows-col-status">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!dataLoaded && tasks.length === 0 ? (
                        <tr className="ows-empty-row">
                          <td colSpan="8">Loading room tasks…</td>
                        </tr>
                      ) : tasks.length === 0 ? (
                        <tr className="ows-empty-row">
                          <td colSpan="8">
                            No rooms assigned yet. Add rooms on the order before printing.
                          </td>
                        </tr>
                      ) : (
                        tasks.map((task, index) => {
                          const status = getStatus(task.TaskStatus);
                          const due = formatDate(task.TaskDueDate || task.taskDueDate || task.DueDate);
                          return (
                            <tr
                              key={task.ID || `task-${index}`}
                              className={status === STATUS.done ? 'is-done' : undefined}
                            >
                              <td className="ows-col-step">{index + 1}</td>
                              <td className="ows-col-room">{task.RoomNo || task.roomNo || ''}</td>
                              <td className="ows-col-task">
                                {task.RoomTask || task.roomTask || task.taskDescription || ''}
                              </td>
                              <td className="ows-col-notes">{formatNotes(task.TaskNotes || task.taskNotes)}</td>
                              <td className="ows-col-notes">{formatNotes(task.QC_Notes || task.qcNotes)}</td>
                              <td className="ows-col-worker">
                                {task.AssignedWorker || task.assignedWorker || task.UserName || ''}
                              </td>
                              <td className="ows-col-due">{due}</td>
                              <td className="ows-col-status">
                                <span className={status.className} title={task.TaskStatus || status.label}>
                                  {status.glyph}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {showSpecs && (
                <section className="ows-specs">
                  <div className="ows-section-head">
                    <h2 className="ows-section-title">Build specifications</h2>
                  </div>
                  <div className="ows-spec-grid">
                    {specs.map((s) => (
                      <div key={s.label} className={`ows-spec${s.wide ? ' ows-spec--wide' : ''}`}>
                        <span className="ows-label">{s.label}</span>
                        <span
                          className={`ows-spec-value${s.mono ? ' ows-mono' : ''}${s.value ? '' : ' is-missing'}`}
                        >
                          {s.value || 'Not set'}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <footer className="ows-footer">
                <span>DiNi Wigs · Order #{orderNo} · Wig code {wigCode}</span>
                <span>Printed {generatedAt} ET</span>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderWorksheet;
